import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import { MatterDetailed } from '../../../types/matters';
import { useMatters } from '../../matters/hooks/useMatters';

interface ClientMattersWidgetProps {
  clientId: string;
  clientName: string;
}

const ClientMattersWidget: React.FC<ClientMattersWidgetProps> = ({ clientId, clientName }) => {
  const navigate = useNavigate();
  const { keycloak } = useKeycloak();
  const { fetchMatters } = useMatters();
  const [matters, setMatters] = useState<MatterDetailed[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<'all' | 'open'>('open');

  // Direct API call - no unstable hook dependencies
  useEffect(() => {
    const fetchClientMatters = async () => {
      if (!clientId || !keycloak?.token) return;

      setLoading(true);
      setError(null);

      try {
        const data = await fetchMatters({ clientId });
        setMatters(data || []);
      } catch (err) {
        console.error('Failed to load contact engagements:', err);
        setError(err instanceof Error ? err.message : 'Failed to load engagements');
        setMatters([]);
      } finally {
        setLoading(false);
      }
    };

    fetchClientMatters();
  }, [clientId, keycloak?.token, fetchMatters]); // Only stable dependencies

  // Filter matters based on active filter
  const filteredMatters = matters.filter(matter => {
    if (activeFilter === 'open') {
      // Check status for open matters (active, pending, on_hold)
      const isOpenStatus = matter.status === 'active' || matter.status === 'pending' || matter.status === 'on_hold';
      // Also check stage for intake or other non-closed stages
      const isOpenStage = matter.stage && matter.stage !== 'closed' && matter.stage !== 'completed';
      
      return isOpenStatus || isOpenStage;
    }
    return true; // 'all' shows everything
  });

  const getStatusColor = (matter: MatterDetailed) => {
    const status: string = matter.status || matter.stage || '';
    
    // Handle both MatterStatus values and stage values
    if (status === 'active') {
      return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-200';
    }
    
    if (status === 'pending' || status === 'intake') {
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-200';
    }
    
    if (status === 'closed' || status === 'completed') {
      return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-200';
    }
    
    if (status === 'on_hold') {
      return 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-200';
    }
    
    // Default color for any other status/stage
    return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-200';
  };

  const handleMatterClick = (matterId: string) => {
    navigate(`/engagements/${matterId}`);
  };

  const handleNewMatter = () => {
    navigate(`/engagements/new?clientId=${clientId}&clientName=${encodeURIComponent(clientName)}`);
  };

  const handleViewAllMatters = () => {
    navigate(`/engagements?clientId=${clientId}`);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
          Contact's engagements
        </h4>
        <button
          onClick={handleNewMatter}
          className="px-3 py-1.5 bg-lex-accent hover:bg-lex-accent-hover text-lex-accent-contrast text-sm font-medium rounded-lg transition-colors duration-200"
        >
          New engagement
        </button>
      </div>

      {/* Filter Tabs */}
      <div className="flex space-x-1 mb-4">
        <button
          onClick={() => setActiveFilter('all')}
          className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors duration-200 ${
            activeFilter === 'all'
              ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
          }`}
        >
          All
        </button>
        <button
          onClick={() => setActiveFilter('open')}
          className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors duration-200 ${
            activeFilter === 'open'
              ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
          }`}
        >
          Open
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-lex-accent"></div>
          <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Loading engagements...</span>
        </div>
      ) : error ? (
        <div className="py-8 text-center">
          <p className="text-sm text-red-600 dark:text-red-400">Failed to load engagements</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{error}</p>
        </div>
      ) : filteredMatters.length === 0 ? (
        <div className="py-8 text-center">
          <div className="text-gray-400 mb-2">
            <svg className="mx-auto h-8 w-8 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            Contact has no {activeFilter} engagements
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-500">No results found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredMatters.map((matter) => (
            <div
              key={matter.id}
              onClick={() => handleMatterClick(matter.id)}
              className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer transition-colors duration-200"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-lex-accent-soft rounded-lg flex items-center justify-center">
                      <svg className="w-4 h-4 text-lex-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {matter.title || matter.description || matter.matter_number || 'Untitled Engagement'}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {matter.practice_area && (
                        <span className="mr-2">{matter.practice_area}</span>
                      )}
                      {matter.matter_number && (
                        <span className="font-mono">#{matter.matter_number}</span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex-shrink-0 ml-3">
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(matter)}`}>
                  {(matter.status || matter.stage || 'active').replace('_', ' ')}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Footer - View All Link */}
      {filteredMatters.length > 0 && (
        <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-600">
          <button
            onClick={handleViewAllMatters}
            className="text-sm text-lex-accent hover:text-lex-accent-hover font-medium"
          >
            View all engagements
          </button>
        </div>
      )}
    </div>
  );
};

export default ClientMattersWidget;
